/*
 * holk-anim.js — dependency-free scroll-animation engine
 * ------------------------------------------------------
 * Mirrors the technique used on holkofficial.com: vanilla Web Animations API
 * reveals triggered by IntersectionObserver, CSS scroll-snap carousels, and a
 * lightweight lightbox. No jQuery, no GSAP. Data-attribute driven so any
 * section can opt in.
 *
 * Reveals:    [data-holk-reveal="fade-up|word-stagger|scale-in"]
 *   optional: data-holk-bidi   (re-animate when scrolled back into view)
 *             data-holk-delay   (ms before the first element animates)
 *             data-holk-stagger (ms between staggered children / words)
 *   children with [data-holk-item] are staggered; otherwise the element itself.
 *
 * Carousel:   [data-holk-carousel]  (a horizontal scroll-snap track)
 *             [data-holk-dots]       (sibling container; dots auto-generated)
 *
 * Lightbox:   [data-holk-lightbox]   (clickable thumb; href/data-full = large img)
 *             grouped by data-holk-lightbox="<groupName>" for prev/next.
 *
 * The signature easing is cubic-bezier(0.16, 1, 0.3, 1) (ease-out-expo feel).
 */
(function () {
  'use strict';

  var EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── Reveal engine ────────────────────────────────────────────────────────
  var revealObserver = null;

  function isMobile() {
    return window.matchMedia('(max-width: 777px)').matches;
  }

  function splitWords(el) {
    if (el.__holkSplit) return el.__holkWordEls;
    var text = el.textContent.trim();
    var words = text.split(/\s+/);
    el.innerHTML = '';
    var wordEls = [];
    words.forEach(function (w, i) {
      var span = document.createElement('span');
      span.style.display = 'inline-block';
      span.style.opacity = '0';
      span.style.willChange = 'transform, opacity';
      span.style.letterSpacing = 'inherit';
      span.textContent = w;
      el.appendChild(span);
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      wordEls.push(span);
    });
    el.style.opacity = '1';
    el.__holkSplit = true;
    el.__holkWordEls = wordEls;
    return wordEls;
  }

  function keyframesFor(preset, distance) {
    switch (preset) {
      case 'scale-in':
        return [
          { opacity: 0, transform: 'scale(0.92)' },
          { opacity: 1, transform: 'scale(1)' }
        ];
      case 'fade-up':
      case 'word-stagger':
      default:
        return [
          { opacity: 0, transform: 'translateY(' + distance + 'px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ];
    }
  }

  function animateIn(el) {
    if (el.__holkAnimating) return;
    el.__holkAnimating = true;

    var preset = el.getAttribute('data-holk-reveal') || 'fade-up';
    var baseDelay = parseInt(el.getAttribute('data-holk-delay') || '0', 10);
    var stagger = parseInt(el.getAttribute('data-holk-stagger') || (preset === 'word-stagger' ? '50' : '90'), 10);
    var duration = parseInt(el.getAttribute('data-holk-duration') || (preset === 'word-stagger' ? '1400' : '1200'), 10);
    var distance = parseInt(el.getAttribute('data-holk-distance') || '45', 10);

    var targets;
    if (preset === 'word-stagger') {
      // Split only the designated heading so sibling content keeps its structure.
      var wordHost = el.querySelector('[data-holk-words]') || el;
      targets = splitWords(wordHost);
    } else {
      var items = el.querySelectorAll('[data-holk-item]');
      targets = items.length ? Array.prototype.slice.call(items) : [el];
    }

    if (reduceMotion) {
      targets.forEach(function (t) { t.style.opacity = '1'; t.style.transform = 'none'; });
      el.classList.add('holk-revealed');
      return;
    }

    targets.forEach(function (t, i) {
      t.animate(keyframesFor(preset, distance), {
        duration: duration,
        delay: baseDelay + i * stagger,
        easing: EASE,
        fill: 'forwards'
      });
    });

    // Optional trailing elements (e.g. subheading, CTA) revealed after the rest.
    var afters = el.querySelectorAll('[data-holk-after]');
    var afterBase = baseDelay + targets.length * stagger + 200;
    Array.prototype.forEach.call(afters, function (after, i) {
      after.animate(
        [{ opacity: 0, transform: 'translateY(18px)' }, { opacity: 1, transform: 'translateY(0)' }],
        { duration: 900, delay: afterBase + i * 120, easing: EASE, fill: 'forwards' }
      );
    });
    el.classList.add('holk-revealed');
  }

  function resetReveal(el) {
    el.__holkAnimating = false;
    el.classList.remove('holk-revealed');
    Array.prototype.forEach.call(el.querySelectorAll('[data-holk-after]'), function (a) {
      a.getAnimations().forEach(function (anim) { anim.cancel(); });
    });
    Array.prototype.forEach.call(el.querySelectorAll('[data-holk-item]'), function (a) {
      a.getAnimations().forEach(function (anim) { anim.cancel(); });
    });
    // Re-hide split words so a bidi word-stagger replays cleanly.
    if (el.__holkWordEls) {
      el.__holkWordEls.forEach(function (w) {
        w.getAnimations().forEach(function (anim) { anim.cancel(); });
        w.style.opacity = '0';
      });
    }
    var host = el.querySelector('[data-holk-words]') || el;
    if (host.__holkWordEls) {
      host.__holkWordEls.forEach(function (w) {
        w.getAnimations().forEach(function (anim) { anim.cancel(); });
        w.style.opacity = '0';
      });
    }
  }

  function ensureRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var el = entry.target;
        if (entry.isIntersecting) {
          animateIn(el);
          if (!el.hasAttribute('data-holk-bidi')) revealObserver.unobserve(el);
        } else if (el.hasAttribute('data-holk-bidi')) {
          resetReveal(el);
        }
      });
    }, { threshold: 0.08, rootMargin: isMobile() ? '0px 0px -10px 0px' : '0px 0px -80px 0px' });
    return revealObserver;
  }

  function initReveals(root) {
    var obs = ensureRevealObserver();
    var els = (root || document).querySelectorAll('[data-holk-reveal]');
    Array.prototype.forEach.call(els, function (el) {
      if (el.__holkObserved) return;
      el.__holkObserved = true;
      obs.observe(el);
      // Fallback: already in view on load.
      var r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) {
        animateIn(el);
        if (!el.hasAttribute('data-holk-bidi')) obs.unobserve(el);
      }
    });
  }

  // ── Scroll-snap carousel ─────────────────────────────────────────────────
  function initCarousels(root) {
    var carousels = (root || document).querySelectorAll('[data-holk-carousel]');
    Array.prototype.forEach.call(carousels, function (track) {
      if (track.__holkCarousel) return;
      track.__holkCarousel = true;

      var slides = Array.prototype.slice.call(track.children);
      var dotsId = track.getAttribute('data-holk-dots');
      var dotsWrap = dotsId ? document.getElementById(dotsId)
        : (track.parentNode.querySelector('[data-holk-dots-for="' + track.id + '"]') || track.parentNode.querySelector('.holk-dots'));

      var dots = [];
      if (dotsWrap) {
        dotsWrap.innerHTML = '';
        slides.forEach(function (slide, i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'holk-dot';
          dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          dot.addEventListener('click', function () {
            slide.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          });
          dotsWrap.appendChild(dot);
          dots.push(dot);
        });
        if (dots[0]) dots[0].classList.add('active');
      }

      if (dots.length) {
        var slideObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var idx = slides.indexOf(entry.target);
            if (idx < 0) return;
            dots.forEach(function (d, i) { d.classList.toggle('active', i === idx); });
          });
        }, { root: track, threshold: 0.5 });
        slides.forEach(function (s) { slideObserver.observe(s); });
      }
    });
  }

  // ── Lightbox ─────────────────────────────────────────────────────────────
  var lb = { backdrop: null, img: null, group: [], index: 0 };

  function buildLightbox() {
    if (lb.backdrop) return;
    var backdrop = document.createElement('div');
    backdrop.className = 'holk-lb-backdrop';
    backdrop.innerHTML =
      '<div class="holk-lb-card" role="dialog" aria-modal="true">' +
      '<button class="holk-lb-close" aria-label="Close">&times;</button>' +
      '<button class="holk-lb-prev" aria-label="Previous">&#8249;</button>' +
      '<img class="holk-lb-img" alt="">' +
      '<button class="holk-lb-next" aria-label="Next">&#8250;</button>' +
      '</div>';
    document.body.appendChild(backdrop);
    lb.backdrop = backdrop;
    lb.img = backdrop.querySelector('.holk-lb-img');

    backdrop.addEventListener('click', function (e) {
      if (e.target === backdrop || e.target.classList.contains('holk-lb-close')) closeLightbox();
    });
    backdrop.querySelector('.holk-lb-prev').addEventListener('click', function () { step(-1); });
    backdrop.querySelector('.holk-lb-next').addEventListener('click', function () { step(1); });
    document.addEventListener('keydown', function (e) {
      if (!lb.backdrop.classList.contains('open')) return;
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') step(-1);
      else if (e.key === 'ArrowRight') step(1);
    });
  }

  function fullSrc(el) {
    return el.getAttribute('data-holk-full') || el.getAttribute('href') ||
      (el.querySelector('img') && el.querySelector('img').src) || el.src;
  }

  function openLightbox(el) {
    buildLightbox();
    var groupName = el.getAttribute('data-holk-lightbox');
    lb.group = Array.prototype.slice.call(
      document.querySelectorAll('[data-holk-lightbox="' + groupName + '"]')
    );
    lb.index = lb.group.indexOf(el);
    lb.img.src = fullSrc(el);
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { lb.backdrop.classList.add('open'); });
    });
  }

  function step(dir) {
    if (!lb.group.length) return;
    lb.index = (lb.index + dir + lb.group.length) % lb.group.length;
    lb.img.src = fullSrc(lb.group[lb.index]);
  }

  function closeLightbox() {
    lb.backdrop.classList.remove('open');
    document.body.style.overflow = '';
  }

  function initLightbox(root) {
    var thumbs = (root || document).querySelectorAll('[data-holk-lightbox]');
    Array.prototype.forEach.call(thumbs, function (el) {
      if (el.__holkLb) return;
      el.__holkLb = true;
      el.style.cursor = 'zoom-in';
      el.addEventListener('click', function (e) {
        if (el.tagName === 'A') e.preventDefault();
        openLightbox(el);
      });
    });
  }

  // ── Hover image-swap ───────────────────────────────────────────────────────
  // A group [data-holk-swap-group] holds a stage <img data-holk-swap-stage> and
  // any number of triggers [data-holk-swap] with data-holk-swap-img="<url>".
  // Hovering a trigger crossfades the stage to that image.
  function initSwaps(root) {
    var groups = (root || document).querySelectorAll('[data-holk-swap-group]');
    Array.prototype.forEach.call(groups, function (group) {
      if (group.__holkSwap) return;
      group.__holkSwap = true;
      var stage = group.querySelector('[data-holk-swap-stage]');
      if (!stage) return;
      var triggers = group.querySelectorAll('[data-holk-swap]');
      var defaultSrc = stage.getAttribute('src');

      function show(src, trigger) {
        if (!src || stage.getAttribute('src') === src) return;
        stage.style.opacity = '0';
        var swap = function () {
          stage.setAttribute('src', src);
          requestAnimationFrame(function () { stage.style.opacity = '1'; });
          stage.removeEventListener('transitionend', swap);
        };
        // Fall back if no transition fires.
        stage.addEventListener('transitionend', swap, { once: true });
        setTimeout(swap, 220);
        Array.prototype.forEach.call(triggers, function (t) { t.classList.toggle('is-active', t === trigger); });
      }

      Array.prototype.forEach.call(triggers, function (t) {
        var src = t.getAttribute('data-holk-swap-img');
        t.addEventListener('mouseenter', function () { show(src, t); });
        t.addEventListener('focus', function () { show(src, t); });
      });
      if (group.hasAttribute('data-holk-swap-reset')) {
        group.addEventListener('mouseleave', function () { show(defaultSrc, null); });
      }
    });
  }

  // ── Boot / refresh ─────────────────────────────────────────────────────────
  function refresh(root) {
    initReveals(root);
    initCarousels(root);
    initLightbox(root);
    initSwaps(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { refresh(document); });
  } else {
    refresh(document);
  }

  // Theme editor: re-scan only the reloaded section.
  document.addEventListener('shopify:section:load', function (e) { refresh(e.target); });

  window.HolkAnim = { refresh: refresh, animateIn: animateIn };
})();
